// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/assessment/ItemRenderer.jsx
//  MICROSERVICIO ENGLISH ASSESSMENT — renderizado de reactivos
//
//  Un componente por formato, todos con el mismo contrato:
//    props: { item, onSubmit(response), disabled }
//    onSubmit entrega la respuesta CRUDA; calificar es tarea del servidor.
//
//  ⚠️  DIFERENCIA CLAVE CON EL ONBOARDING: aquí NO se revela si acertó.
//      En una calibración de venta el feedback inmediato motiva; en un examen
//      de nivel enseña la respuesta y contamina los reactivos siguientes —
//      además de invitar a reintentar hasta adivinar. El alumno sabe cómo le
//      fue al final, en la radiografía.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { speak, cancelSpeech, isSpeechAvailable } from '../../utils/browserSpeech';
import { WritingItem, SpeakingItem } from './ProductiveItems';

const LETTERS = ['a', 'b', 'c', 'd'];

/* ─────────────────────────────────────────────────────────────────────────────
   Opción múltiple — sirve a Use of English y, con texto, a Reading
   ───────────────────────────────────────────────────────────────────────────── */

function ChoiceItem({ item, onSubmit, disabled }) {
  const [picked, setPicked] = useState(null);
  const { payload } = item;

  useEffect(() => setPicked(null), [item.item_id]);

  const choose = (id) => {
    if (disabled) return;
    setPicked(id);
    onSubmit(id);
  };

  return (
    <>
      {payload.text && <blockquote className="as-passage">{payload.text}</blockquote>}
      <p className="as-prompt">{payload.question || payload.prompt}</p>

      <div className="as-choices">
        {payload.options.map((opt, i) => (
          <button
            key={opt.id}
            type="button"
            className={`as-choice${picked === opt.id ? ' as-picked' : ''}`}
            onClick={() => choose(opt.id)}
            disabled={disabled}
          >
            <span className="as-choice-key">{LETTERS[i]}</span>
            <span>{opt.text}</span>
          </button>
        ))}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Ordena la frase — tap-to-place (mobile-first, sin dependencias)
   ───────────────────────────────────────────────────────────────────────────── */

function OrderItem({ item, onSubmit, disabled }) {
  const { payload } = item;

  // Se baraja en el cliente además de venir barajado del banco: doble seguro
  // para que el orden de almacenamiento nunca insinúe la respuesta.
  const chips = useMemo(() => {
    const arr = payload.chips.map((word, idx) => ({ word, idx }));
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [payload.chips]);

  const [placed, setPlaced] = useState([]);
  useEffect(() => setPlaced([]), [item.item_id]);

  const isPlaced = (idx) => placed.includes(idx);
  const place = (idx) => !disabled && !isPlaced(idx) && setPlaced((p) => [...p, idx]);
  const unplace = (idx) => !disabled && setPlaced((p) => p.filter((x) => x !== idx));

  const complete = placed.length === payload.chips.length;

  return (
    <>
      <p className="as-prompt">{payload.prompt}</p>

      <div className="as-slots">
        {placed.length === 0 ? (
          <span className="as-slots-empty">Toca las palabras en orden</span>
        ) : (
          placed.map((idx) => (
            <button
              key={`p-${idx}`}
              type="button"
              className="as-chip as-chip-set"
              onClick={() => unplace(idx)}
              disabled={disabled}
            >
              {payload.chips[idx]}
            </button>
          ))
        )}
      </div>

      <div className="as-pool">
        {chips.map(({ word, idx }) =>
          isPlaced(idx) ? null : (
            <button
              key={`c-${idx}`}
              type="button"
              className="as-chip"
              onClick={() => place(idx)}
              disabled={disabled}
            >
              {word}
            </button>
          )
        )}
      </div>

      <button
        type="button"
        className="as-btn"
        disabled={!complete || disabled}
        onClick={() => onSubmit(placed.map((i) => payload.chips[i]))}
      >
        Confirmar respuesta
      </button>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Encuentra el error — un solo toque, sin reintentos
   ───────────────────────────────────────────────────────────────────────────── */

function ErrorItem({ item, onSubmit, disabled }) {
  const [picked, setPicked] = useState(null);
  const { payload } = item;

  useEffect(() => setPicked(null), [item.item_id]);

  const choose = (i) => {
    if (disabled || picked !== null) return;
    setPicked(i);
    onSubmit(String(i));
  };

  return (
    <>
      <p className="as-prompt">Toca la palabra que está mal:</p>
      <div className="as-sentence">
        {payload.words.map((w, i) => (
          <button
            key={`${item.item_id}-w-${i}`}
            type="button"
            className={`as-word${picked === i ? ' as-picked' : ''}`}
            onClick={() => choose(i)}
            disabled={disabled || picked !== null}
          >
            {w}
          </button>
        ))}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Escucha — audio con Web Speech, una repetición permitida
   ───────────────────────────────────────────────────────────────────────────── */

function ListenItem({ item, onSubmit, disabled, tone = false }) {
  const { payload } = item;
  const [playing, setPlaying] = useState(false);
  const [heard, setHeard] = useState(false);
  const [replays, setReplays] = useState(0);
  const [picked, setPicked] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const MAX_REPLAYS = 1;

  useEffect(() => {
    setPlaying(false); setHeard(false); setReplays(0);
    setPicked(null); setUnavailable(false);
    return () => cancelSpeech();
  }, [item.item_id]);

  const play = () => {
    if (playing || (heard && replays >= MAX_REPLAYS)) return;

    if (!isSpeechAvailable()) {
      // Degradación honesta: si el navegador no puede hablar, se revela el
      // texto. Es preferible medir con una ventaja conocida a bloquear a un
      // alumno que no puede continuar el examen.
      setUnavailable(true);
      setHeard(true);
      return;
    }

    if (heard) setReplays((r) => r + 1);
    speak(payload.audio, payload.voice, {
      onStart: () => setPlaying(true),
      onEnd: () => { setPlaying(false); setHeard(true); },
      onError: () => { setPlaying(false); setUnavailable(true); setHeard(true); },
    });
  };

  const choose = (id) => {
    if (disabled) return;
    setPicked(id);
    onSubmit(id);
  };

  const left = MAX_REPLAYS - replays;

  return (
    <>
      <div className="as-audio">
        <button
          type="button"
          className={`as-play${playing ? ' as-playing' : ''}`}
          onClick={play}
          disabled={playing || (heard && left <= 0)}
          aria-label={playing ? 'Reproduciendo' : 'Reproducir audio'}
        >
          <span className="as-wave" aria-hidden="true" />
          <span className="as-wave" aria-hidden="true" />
          <span aria-hidden="true">{playing ? '🔊' : '▶'}</span>
        </button>

        {!heard && !playing && <p className="as-hint">Toca para escuchar</p>}
        {heard && left > 0 && !playing && (
          <button type="button" className="as-replay" onClick={play}>
            Escuchar otra vez ({left})
          </button>
        )}
        {heard && left <= 0 && !playing && !unavailable && (
          <p className="as-hint">Ya usaste tu repetición</p>
        )}
        {unavailable && (
          <p className="as-hint">
            Tu navegador no reproduce audio. El texto era:<br />
            <strong>“{payload.audio}”</strong>
          </p>
        )}
      </div>

      {heard && (
        <>
          <p className="as-prompt">{payload.question}</p>
          <div className={tone ? 'as-tones' : 'as-choices'}>
            {payload.options.map((opt, i) => (
              <button
                key={opt.id}
                type="button"
                className={`${tone ? 'as-tone' : 'as-choice'}${picked === opt.id ? ' as-picked' : ''}`}
                onClick={() => choose(opt.id)}
                disabled={disabled}
              >
                {!tone && <span className="as-choice-key">{LETTERS[i]}</span>}
                <span>{opt.text}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Despachador
   ───────────────────────────────────────────────────────────────────────────── */

const RENDERERS = {
  choice: ChoiceItem,
  reading_choice: ChoiceItem,
  order: OrderItem,
  error: ErrorItem,
  listen_choice: (p) => <ListenItem {...p} />,
  listen_tone: (p) => <ListenItem {...p} tone />,
  // Productivas [v2]: no hay clave, el servidor las manda a evaluar.
  writing_prompt: WritingItem,
  speaking_prompt: SpeakingItem,
};

export default function ItemRenderer({ item, onSubmit, disabled }) {
  const Renderer = RENDERERS[item.format];

  if (!Renderer) {
    // El banco creció con un formato que esta versión no sabe pintar. Se avisa
    // y se permite saltarlo en vez de dejar el examen atascado.
    return (
      <div className="as-fallback">
        <p>Este reactivo usa un formato que esta versión aún no muestra.</p>
        <button type="button" className="as-btn" onClick={() => onSubmit(null)}>
          Saltar
        </button>
      </div>
    );
  }

  return <Renderer item={item} onSubmit={onSubmit} disabled={disabled} />;
}
